import { useRouter } from 'helpers/navigation'
import React, { useCallback, type JSX } from 'react'
import styled from 'styled-components'
import { HeadingL, HeadingXS } from 'uiComponents/atoms/Text/Headings'
import { complementaryColor, shade30 } from 'uiComponents/constants/colors'
import {
  mediaExtraLargeScreens,
  mediaMinL,
} from 'uiComponents/constants/customMedia'
import { fsM, fsS, fsXL } from 'uiComponents/constants/typography'
import { StyledProductImage } from '../ProductImage/StyledProductImage'

const PRODUCT_TILE_REGULAR_HEIGHT = 120
const PRODUCT_TILE_LARGE_HEIGHT = 184

const TextWrapper = styled.div`
  position: absolute;
  width: 75%;
  bottom: 40px;
  left: 30px;
  transition: transform 250ms ease-in-out;
  transition-delay: 100ms;
`
const SubTitle = styled(HeadingXS)`
  color: ${complementaryColor};
  z-index: 1;

  @media ${mediaExtraLargeScreens} {
    font-size: ${fsS}px;
  }

  @media ${mediaMinL} {
    font-size: ${fsM}px;
  }
`
const Title = styled(HeadingL)`
  hyphens: auto;
  color: ${complementaryColor};
  z-index: 1;

  @media ${mediaMinL} {
    font-size: ${fsXL}px;
  }
`

const ShadeFallback = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  background-color: ${shade30};
`

const StoryBackgroundModern = styled.img`
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  object-fit: cover;
  object-position: center 0;
  filter: brightness(90%);
`

const StoryBackgroundWrapper = styled.div<{ src: string }>`
  position: absolute;
  background-image: ${({ src }) => `url(${src})`};
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center 0;
  transition: transform 300ms ease;
  transform-origin: center;
  top: 0;
  height: 100%;
  width: 100%;

  > ${StoryBackgroundModern} {
    opacity: 0;
  }

  @supports (object-fit: cover) {
    background-image: none !important;
    > ${StoryBackgroundModern} {
      opacity: 1;
    }
    > ${ShadeFallback} {
      display: none;
    }
  }
`

const ProductTileWrapper = styled.div<{ largeTile?: boolean }>`
  position: absolute;
  width: 100%;
  height: ${({ largeTile }) =>
    largeTile
      ? `${PRODUCT_TILE_LARGE_HEIGHT}px`
      : `${PRODUCT_TILE_REGULAR_HEIGHT}px`};
  bottom: ${({ largeTile }) =>
    largeTile
      ? `-${PRODUCT_TILE_LARGE_HEIGHT}px`
      : `-${PRODUCT_TILE_REGULAR_HEIGHT}px`};
  z-index: 1;
  background-color: ${complementaryColor};
  transform: translateZ(0);
  transition: transform 250ms ease-in-out;
  transition-delay: 100ms;

  display: flex;
  padding-top: ${({ largeTile }) => (largeTile ? '20px' : '10px')};
  flex-wrap: nowrap;

  & > ${StyledProductImage} {
    margin: 0 2px;
    height: 100%;
    padding-top: 0;
  }
`

const StoryTileButton = styled.button`
  position: relative;
  display: block;
  width: 100%;
  height: 100%;
  text-align: left;
`

export const StoryTileSelector = styled.div<{
  largeTile?: boolean
}>`
  position: relative;
  z-index: 0;
  display: block;
  cursor: pointer;
  overflow: hidden;

  &:hover {
    ${TextWrapper}, ${ProductTileWrapper} {
      transform: ${({ largeTile }) =>
        largeTile
          ? `translateY(-${PRODUCT_TILE_LARGE_HEIGHT}px)`
          : `translateY(-${PRODUCT_TILE_REGULAR_HEIGHT}px)`};
      transition-delay: 0ms;
    }
    ${StoryBackgroundWrapper} {
      transform: scale(1.03);
    }
  }
`

interface StoryTileProps {
  href: string
  src: string
  srcSet?: string
  title: string
  subTitle: string
  onMouseEnter: () => void
  productTiles?: JSX.Element[]
  largeTile?: boolean
  onLinkClick?: () => void
  importance?: 'high' | 'low'
}

const StoryTile = ({
  href,
  src,
  srcSet,
  title,
  subTitle,
  onMouseEnter,
  productTiles,
  importance,
  onLinkClick,
  largeTile = false,
  ref,
}: StoryTileProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const { navigateClient } = useRouter()
  const handleNavigateClient = useCallback(navigateClient(href), [href])

  const handleStoryClick = useCallback(() => {
    handleNavigateClient()
    if (onLinkClick) {
      onLinkClick()
    }
  }, [onLinkClick])

  return (
    <StoryTileSelector
      ref={ref}
      data-testid={largeTile ? 'SquareBig' : 'SquareSmall'}
      onMouseEnter={onMouseEnter}
      largeTile={largeTile}
    >
      <StoryTileButton onClick={handleStoryClick}>
        <StoryBackgroundWrapper data-testid="BackgroundImage" src={src}>
          <StoryBackgroundModern
            data-testid="Image"
            loading="lazy"
            importance={importance}
            src={src}
            srcSet={srcSet}
          />
          <ShadeFallback data-testid="OverlayBlack" />
        </StoryBackgroundWrapper>
        <TextWrapper>
          <SubTitle data-testid="Subline">{subTitle}</SubTitle>
          <Title data-testid="Headline">{title}</Title>
        </TextWrapper>
      </StoryTileButton>
      <ProductTileWrapper
        data-testid="ProductImageHoverView"
        largeTile={largeTile}
      >
        {productTiles}
      </ProductTileWrapper>
    </StoryTileSelector>
  )
}

export default StoryTile
